import { trimString } from './string';
import { isString } from './util';

/**
 * Parses a string in csv format to an array
 * @param csv the string to be parsed
 * @param tableDefinition the shape of the tables to be exported
 * tableDefinition.title matches the I row
 * tableDefinition.field shapes the object
 * @param dateFormat the dateFormat that should be used for the date parsing, by default date format is 
 * 'YYYY-MM-DDTHH:mm:ss.SSSZZ'
 * @param toDateProperties the list of properties which should be parsed with the date format above
 * @return array with objects containing the properties in the header line
 */
export function csvToArray(
  csv: string,
  tableDefinition: Array<{ title: string[]; field: string[] }> = [],
  dateFormat?: string,
  ...toDateProperties: string[]
): any[] {
  let lines: string[] = csv.trim().split("\n");
  let properties: any[] = [];
  let data: any[] = [];
  if (dateFormat === undefined) {
    dateFormat = "YYYY-MM-DDTHH:mm:ss.SSSZZ";
  }

  for (let x = 0; x < lines.length; x++) {
    let row: string[] = lines[x].trim().split(",");
    let model: any = {};
    const propertiesCur = lines[x].trim().split(",");
    // identify I rows for shaping else it is a D row
    if (propertiesCur[0] === "I") {
      // change the shape for rows base on param shape
      const curTableDefinition = tableDefinition.find(item => lines[x].trim().indexOf(item.title.join(",")) === 0);
      if (curTableDefinition) {
        properties = curTableDefinition.field;
      } else {
        // reshape properties base on the I row value
        properties = lines[x].trim().split(",");
      }
    } else if (x === 0 && propertiesCur[0] !== "D") {
      // Add default properties for standard CSV format
      properties = lines[0].trim().split(',');
    } else {
      // only push D rows
      for (let propertyIndex = 0; propertyIndex < properties.length; propertyIndex++) {
        let propertyname = trimString(properties[propertyIndex], '"');
        if (toDateProperties.includes(propertyname)) {
          model[propertyname] = parseCsvDate(trimString(row[propertyIndex], '"'));
        } else {
          model[propertyname] = trimString(row[propertyIndex], '"');
        }
      }
      data.push(model);
    }
  }
  return data;
}

export function parseCsvDate(input: string): Date {
  if (!isString(input) || input.length !== 19) return new Date();

  let year = parseInt(input.substring(0, 4));
  let month = parseInt(input.substring(5, 7)) - 1;
  let date = parseInt(input.substring(8, 10));
  let hours = parseInt(input.substring(11, 13));
  let minutes = parseInt(input.substring(14, 16));
  let seconds = parseInt(input.substring(17, 19));
  return new Date(year, month, date, hours, minutes, seconds);
}

export function arrayToCsv(rows: Array<any>, columns: Array<CsvColumn> | Array<string>, nemFormat?: Array<string> | boolean, dateFormat?: string): string {
  let delimiter = ',';
  let regExFormat = new RegExp("[\"" + delimiter + "\n\r]");

  // Format property value
  let formatCsvValue = function (value: any) {
    return value == null ? ""
      : regExFormat.test(value += "") ? "\"" + value.replace(/"/g, "\"\"") + "\""
        : value;
  }

  let formatCsvColumn = function (value: any) {
    return formatCsvValue(value.title ? value.title : value.field);
  }

  for (let column of columns) {
    if (typeof column == 'string') {
      column = { field: column };
    }
  }

  if (!columns) columns = uniqueColumns(rows);
  let csvColumns = (columns as Array<CsvColumn>).map(formatCsvColumn).join(delimiter);
  let nemColumns = '';

  if (nemFormat) {

    if ((nemFormat as Array<string>).length) {
      nemColumns = (nemFormat as Array<string>).map(formatCsvValue).join(delimiter).toUpperCase();
      if (csvColumns.length > 0) nemColumns += ",";
    }
    csvColumns = 'I,' + nemColumns + csvColumns.toUpperCase();
  }

  return [csvColumns].concat(rows.map(function (row) {
    let csvRow = (columns as Array<CsvColumn>).map(function (column) {
      let value = row[column.field];
      return formatCsvValue(column.formatter ? column.formatter.call(value, row, column) : value);
    }).join(delimiter);

    if (nemFormat) {
      csvRow = 'D,' + nemColumns + csvRow;
    }
    return csvRow;
  })).join("\n");
}

// Get unique columns from all rows
function uniqueColumns(rows) {
  let columnSet = {};
  let columns: Array<CsvColumn> = [];

  for (let row of rows) {
    for (let column in row) {
      if (!(column in columnSet)) {
        columns.push({ field: columnSet[column] = column });
      }
    }
  }

  return columns;
}

export interface CsvColumn {
  field: string;
  title?: string;
  formatter?: (value: any, row: any, column: CsvColumn) => string;
  data?: any;
}




// WEBPACK FOOTER //
// src/../~/wc-lib/src/wc-converters/csv.ts